import useUrlState from '@ahooksjs/use-url-state'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, DatePicker, Select } from 'antd'
import moment from 'moment'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { DepartmentPeopleWorkResourceContainer } from '@/components/work-resource/department/department-people-work-resource.container'
import { DepartmentProjectWorkResourceContainer } from '@/components/work-resource/department/department-project-work-resource.container'
import { Team } from '@/schemas'

enum ViewEnum {
  Project = 'project',
  People = 'people',
}

export const WorkResourceContainer: React.FC<{ team: Team }> = ({ team }) => {
  const ref = React.useRef<{ refresh: () => void }>(null)

  const [view, setView] = React.useState<ViewEnum>(ViewEnum.People)

  const initMomentDate = moment().isoWeekday() < 3 ? moment().subtract(1, 'week') : moment()

  const [weekDate, setWeekDate] = React.useState<{
    year: number
    week: number
    startDate: string
    endDate: string
  }>({
    year: initMomentDate.isoWeekYear(),
    week: initMomentDate.isoWeek(),
    startDate: initMomentDate.startOf('isoWeek').format('YYYY-MM-DD'),
    endDate: initMomentDate.endOf('isoWeek').format('YYYY-MM-DD'),
  })

  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const urlYear = urlParams.get('year')
  const urlWeek = urlParams.get('week')
  const urlViewType = urlParams.get('viewType')
  const [state, setState] = useUrlState({
    year: urlYear || initMomentDate.isoWeekYear(),
    week: urlWeek || initMomentDate.isoWeek(),
    viewType: urlViewType || ViewEnum.People,
  })

  React.useEffect(() => {
    // 防止 invalid year, week
    const year = !urlYear || isNaN(Number(urlYear)) ? initMomentDate.isoWeekYear() : Number(urlYear)
    const week = !urlWeek || isNaN(Number(urlWeek)) ? initMomentDate.isoWeek() : Number(urlWeek)
    const date = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    // 防止越界 week, 如 week=60
    setState({
      year: date.isoWeekYear(),
      week: date.isoWeek(),
      viewType: urlViewType === ViewEnum.Project ? ViewEnum.Project : ViewEnum.People,
    })
  }, [])

  React.useEffect(() => {
    if (state.year && state.week && state.viewType) {
      const year = Number(state.year)
      const week = Number(state.week)
      const startDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek').format('YYYY-MM-DD')
      const endDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).endOf('isoWeek').format('YYYY-MM-DD')
      setWeekDate({ year, week, startDate, endDate })
      setView(state.viewType)
    }
  }, [state])

  return (
    <div>
      <div className='flex my-3 justify-between'>
        <div className='flex gap-4'>
          <DatePicker
            allowClear={false}
            picker='week'
            format={`${weekDate.startDate}-${weekDate.endDate} (GGGG-Wo)`}
            value={moment(weekDate.startDate)}
            onChange={(value) => {
              if (value) {
                const year = value.isoWeekYear()
                const week = value.isoWeek()
                setState({ year, week })
              }
            }}
          />
          <Button
            onClick={() => {
              const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).subtract(1, 'week')
              const year = yearWeek.isoWeekYear()
              const week = yearWeek.isoWeek()
              setState({ year, week })
            }}
            icon={<LeftOutlined />}
          />
          <Button
            onClick={() => {
              const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).add(1, 'week')
              const year = yearWeek.isoWeekYear()
              const week = yearWeek.isoWeek()
              setState({ year, week })
            }}
            icon={<RightOutlined />}
          />
        </div>
        <div>
          <span>视图</span>
          <Select
            className='ml-2'
            size='small'
            value={view}
            onChange={(value) => setState({ viewType: value })}
            options={[ViewEnum.Project, ViewEnum.People].map((value) => {
              return {
                label: value === ViewEnum.People ? '成员资源视角' : '项目资源视角',
                value,
              }
            })}
          />
        </div>
      </div>
      {view === 'project' ? (
        <DepartmentProjectWorkResourceContainer
          key='project'
          ref={ref}
          team={team}
          weekDate={weekDate}
          startDate={weekDate.startDate}
          endDate={weekDate.endDate}
        />
      ) : (
        <DepartmentPeopleWorkResourceContainer
          key='people'
          ref={ref}
          team={team}
          weekDate={weekDate}
          startDate={weekDate.startDate}
          endDate={weekDate.endDate}
        />
      )}
    </div>
  )
}
